import {
  IResult,
  ICategorizedResponse,
  ActivitiesEntryTypeEnum,
  ActivityTagKindValue,
} from "redmond";

import * as actionTypes from "./constants";
import {
  TripDestination,
  CombinedSearchGroup,
  CombinedAutocompleteRequest,
} from "../types";

export interface ISetFromDate {
  type: actionTypes.SET_FROM_DATE;
  date: Date | null;
}

export const setFromDate = (date: Date | null): ISetFromDate => ({
  type: actionTypes.SET_FROM_DATE,
  date,
});

export interface ISetUntilDate {
  type: actionTypes.SET_UNTIL_DATE;
  date: Date | null;
}

export const setUntilDate = (date: Date | null): ISetUntilDate => ({
  type: actionTypes.SET_UNTIL_DATE,
  date,
});

export interface IFetchLocationCategories {
  type: actionTypes.FETCH_LOCATION_CATEGORIES;
  queryString: string;
}

export const fetchLocationCategories = (
  queryString: string
): IFetchLocationCategories => ({
  type: actionTypes.FETCH_LOCATION_CATEGORIES,
  queryString,
});

export interface ISetLocationCategories {
  type: actionTypes.SET_LOCATION_CATEGORIES;
  categories: ICategorizedResponse[];
}

export const setLocationCategories = (
  categories: ICategorizedResponse[]
): ISetLocationCategories => ({
  type: actionTypes.SET_LOCATION_CATEGORIES,
  categories,
});

export interface ISetLocation {
  type: actionTypes.SET_LOCATION;
  location: IResult | null;
}

export const setLocation = (location: IResult | null): ISetLocation => ({
  type: actionTypes.SET_LOCATION,
  location,
});

export interface ISetLocationAutocompleteError {
  type: actionTypes.SET_LOCATION_AUTOCOMPLETE_ERROR;
  error: boolean;
}

export const setLocationAutocompleteError = (
  error: boolean
): ISetLocationAutocompleteError => ({
  type: actionTypes.SET_LOCATION_AUTOCOMPLETE_ERROR,
  error,
});

export interface ISetCategoryTagId {
  type: actionTypes.SET_CATEGORY_TAG_ID;
  categoryTagId: number | null;
  categoryTagKind: ActivityTagKindValue | null;
}

export const setCategoryTagId = (
  categoryTagId: number | null,
  categoryTagKind: ActivityTagKindValue | null = "Category"
): ISetCategoryTagId => ({
  type: actionTypes.SET_CATEGORY_TAG_ID,
  categoryTagId,
  categoryTagKind: categoryTagId ? categoryTagKind : null,
});

export interface ISetEntryType {
  type: actionTypes.SET_ENTRY_TYPE;
  entryType: ActivitiesEntryTypeEnum;
}

export const setEntryType = (
  entryType: ActivitiesEntryTypeEnum
): ISetEntryType => ({
  type: actionTypes.SET_ENTRY_TYPE,
  entryType,
});

export interface ISetEntryTypesAll {
  type: actionTypes.SET_ENTRY_TYPES_ALL;
  entryTypesAll: ActivitiesEntryTypeEnum[];
}

export const setEntryTypesAll = (
  entryTypesAll: ActivitiesEntryTypeEnum[]
): ISetEntryTypesAll => ({
  type: actionTypes.SET_ENTRY_TYPES_ALL,
  entryTypesAll,
});

export interface ISetLodgingId {
  type: actionTypes.SET_LODGING_ID;
  lodgingId: string | null;
}

export const setLodgingId = (lodgingId: string | null): ISetLodgingId => ({
  type: actionTypes.SET_LODGING_ID,
  lodgingId,
});

export interface ISetStaysAdvance {
  type: actionTypes.SET_STAYS_ADVANCE;
  staysAdvance: number | null;
}

export const setStaysAdvance = (staysAdvance: number | null): ISetStaysAdvance => ({
  type: actionTypes.SET_STAYS_ADVANCE,
  staysAdvance,
});

// Trip destinations actions
export interface IFetchTripDestinations {
  type: actionTypes.FETCH_TRIP_DESTINATIONS;
}

export const fetchTripDestinations = (): IFetchTripDestinations => ({
  type: actionTypes.FETCH_TRIP_DESTINATIONS,
});

export interface ISetTripDestinations {
  type: actionTypes.SET_TRIP_DESTINATIONS;
  destinations: TripDestination[];
}

export const setTripDestinations = (
  destinations: TripDestination[]
): ISetTripDestinations => ({
  type: actionTypes.SET_TRIP_DESTINATIONS,
  destinations,
});

export interface ISetTripDestinationsLoading {
  type: actionTypes.SET_TRIP_DESTINATIONS_LOADING;
  loading: boolean;
}

export const setTripDestinationsLoading = (
  loading: boolean
): ISetTripDestinationsLoading => ({
  type: actionTypes.SET_TRIP_DESTINATIONS_LOADING,
  loading,
});

export interface ISetTripDestinationsError {
  type: actionTypes.SET_TRIP_DESTINATIONS_ERROR;
  error: string | null;
}

export const setTripDestinationsError = (
  error: string | null
): ISetTripDestinationsError => ({
  type: actionTypes.SET_TRIP_DESTINATIONS_ERROR,
  error,
});

export interface ISetTripDestinationsLoaded {
  type: actionTypes.SET_TRIP_DESTINATIONS_LOADED;
  loaded: boolean;
}

export const setTripDestinationsLoaded = (
  loaded: boolean
): ISetTripDestinationsLoaded => ({
  type: actionTypes.SET_TRIP_DESTINATIONS_LOADED,
  loaded,
});

// Combined autocomplete actions
export interface IFetchCombinedAutocomplete {
  type: actionTypes.FETCH_COMBINED_AUTOCOMPLETE;
  request: CombinedAutocompleteRequest;
}

export const fetchCombinedAutocomplete = (
  request: CombinedAutocompleteRequest
): IFetchCombinedAutocomplete => ({
  type: actionTypes.FETCH_COMBINED_AUTOCOMPLETE,
  request,
});

export interface ISetCombinedAutocompleteResults {
  type: actionTypes.SET_COMBINED_AUTOCOMPLETE_RESULTS;
  groups: CombinedSearchGroup[];
}

export const setCombinedAutocompleteResults = (
  groups: CombinedSearchGroup[]
): ISetCombinedAutocompleteResults => ({
  type: actionTypes.SET_COMBINED_AUTOCOMPLETE_RESULTS,
  groups,
});

export interface ISetCombinedAutocompleteError {
  type: actionTypes.SET_COMBINED_AUTOCOMPLETE_ERROR;
  error: boolean;
}

export const setCombinedAutocompleteError = (
  error: boolean
): ISetCombinedAutocompleteError => ({
  type: actionTypes.SET_COMBINED_AUTOCOMPLETE_ERROR,
  error,
});

export interface IClearCombinedAutocomplete {
  type: actionTypes.CLEAR_COMBINED_AUTOCOMPLETE;
}

export const clearCombinedAutocomplete = (): IClearCombinedAutocomplete => ({
  type: actionTypes.CLEAR_COMBINED_AUTOCOMPLETE,
});

export type ExperiencesSearchActions =
  | IFetchLocationCategories
  | ISetLocationCategories
  | ISetLocation
  | ISetLocationAutocompleteError
  | ISetFromDate
  | ISetUntilDate
  | ISetCategoryTagId
  | ISetEntryType
  | ISetEntryTypesAll
  | ISetLodgingId
  | ISetStaysAdvance
  | IFetchTripDestinations
  | ISetTripDestinations
  | ISetTripDestinationsLoading
  | ISetTripDestinationsError
  | ISetTripDestinationsLoaded
  | IFetchCombinedAutocomplete
  | ISetCombinedAutocompleteResults
  | ISetCombinedAutocompleteError
  | IClearCombinedAutocomplete;
